import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Activity, Globe2, Network, PhoneCall, Plug } from "lucide-react";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const features = [
  { title: "Direct Interconnects", desc: "Voice traffic routed through direct carrier interconnects, not resold capacity, for consistent call quality.", Icon: Network },
  { title: "Global Termination Coverage", desc: "Terminate calls across hundreds of destinations worldwide through premium and direct routes.", Icon: Globe2 },
  { title: "Carrier-Grade Reliability", desc: "Redundant infrastructure and real-time monitoring keep call completion rates high, even at peak volume.", Icon: Activity },
  { title: "Flexible Integration", desc: "Standard SIP trunking setup, compatible with existing PBX and softswitch infrastructure.", Icon: Plug },
];

const steps = [
  "Connect via SIP trunk to our network",
  "Traffic routes through optimized, direct voice paths",
  "Monitor call quality, ASR/ACD, and volume in real time through your dashboard",
];

const SolutionVoiceTermination = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="Wholesale Voice Termination Provider | Yootelco"
        description="Global wholesale voice termination with direct carrier routes. Competitive rates and reliable call quality for operators and enterprises."
        path="/solutions/voice-termination"
      />
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[900px] text-center">
            <h1 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px]">
              Wholesale Voice &amp; SIP Trunking, Built for Scale
            </h1>
            <p className="mx-auto mt-[18px] max-w-[680px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px]">
              Direct, carrier-grade voice connectivity for MNOs, enterprises, and service providers, engineered for high-volume traffic and global reach.
            </p>
            <div className="mt-[28px] flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-shell pt-[56px] md:pt-[72px]">
        <div className="mx-auto grid max-w-[1200px] auto-rows-fr gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <article key={f.title} className="flex h-full flex-col rounded-[16px] border border-border/70 bg-card p-[22px] shadow-card">
              <span className="flex h-[44px] w-[44px] flex-none items-center justify-center rounded-[10px] bg-primary/10 text-primary" aria-hidden="true">
                <f.Icon className="h-5 w-5" strokeWidth={2.3} />
              </span>
              <h3 className="mt-[16px] text-[15px] font-bold leading-[1.3] tracking-tight text-foreground">{f.title}</h3>
              <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{f.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">How It Works</h2>
        </div>
        <div className="mx-auto mt-[32px] grid max-w-[1120px] auto-rows-fr gap-5 md:grid-cols-3">
          {steps.map((s, i) => (
            <article key={s} className="flex h-full flex-col rounded-[16px] border border-border/70 bg-secondary/40 p-[24px]">
              <span className="flex h-[40px] w-[40px] flex-none items-center justify-center rounded-full bg-primary text-[15px] font-bold text-primary-foreground shadow-soft">
                {i + 1}
              </span>
              <p className="mt-[16px] text-[14.5px] font-semibold leading-[1.55] text-foreground">{s}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
            <CTASection
        title="Ready to scale your voice traffic?"
        useCase="Voice Termination"
        buttonIcon={<PhoneCall className="h-4 w-4" strokeWidth={2.4} />}
        showUnderline={false}
      />

      <SiteFooter />
    </main>
  );
};

export default SolutionVoiceTermination;
